import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import { ComponentPage, DemoCard } from '../../shared';
import { DEMO_PRODUCTS, DEMO_PACKAGES, HERO_IMAGE, LISTING, LISTING_FRAME_MARK } from './contentDemoData';

/* OVER/LL listing grid (Figma OVER-LL 2699:18385 / 2707:18924 / 2699:16566):
   3-up, 32px gaps both ways, the brand glyph on the card frames */
const listingProps = (brand) => ({
  brand,
  caption: LISTING.caption,
  captionStyle: 'body',
  title: LISTING.title,
  align: 'left',
  desktopColumns: 3,
  rowGap: { base: 'medium-m', sm: 'medium-m', md: 'medium-l' },
  cardProps: { frameMark: LISTING_FRAME_MARK },
});

export const meta = {
  component: ProductsGrid,
  title: 'Products Grid',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/ContentBlocks/ProductsGrid',
  figmaNode: '3871:1020',
  description:
    'Responsive grid of ProductCards with a heading and CTA. The desktop column count is automatic — exactly three cards get a three-up row, anything else four-up — and an odd number of cards on mobile stretches the last card across both columns so no orphan is left.',
  props: [
    ['brand', "'CLS' | 'ABC' | 'Overall'", '—', 'Sets data-brand on the section — a dashboard / preview tool; pages set the brand once at the root (useBrandScope), never per section'],
    ['theme', "'light' | 'dark'", "'light'", 'Dark inverts the heading colours'],
    ['caption', 'string', '—', 'Caption above the title (typo-h6, gray-5)'],
    ['captionVariant', "'default' | 'success' | 'error'", "'default'", 'Caption colour — success (e.g. SPAR OP TIL 20%) / error (e.g. TRENDING)'],
    ['captionStyle', "'h6' | 'body'", "'h6'", 'Caption type — h6, or P.Regular uppercase gray-6 (the OVER/LL eyebrow). A prop, not a brand rule'],
    ['align', "'left' | 'center' | 'right'", "'center'", 'Alignment of the title block (caption + title + subtitle)'],
    ['title', 'string', '—', 'Section heading (typo-h2)'],
    ['subtitle', 'string', '—', 'Section subheading'],
    ['ctaLabel', 'string', '—', 'CTA label; omit it and no button renders'],
    ['ctaVariant', 'string', 'auto', "'white' on dark, 'outline' on light"],
    ['ctaSize', 'string', "'primary'", 'Button size'],
    ['ctaIcon', 'node', '—', 'CTA icon'],
    ['ctaHref', 'string', '—', 'CTA link target'],
    ['ctaOnClick', 'func', '—', 'CTA handler'],
    ['paddingTop', 'string', "'large-m'", 'DS spacing token name'],
    ['paddingBottom', 'string', "'large-m'", 'DS spacing token name'],
    ['headerGap', 'string', "'large-s'", 'Space between the header and the grid — DS spacing token name or CSS value (OVER/LL listing: "medium-l", Figma 2710:25040)'],
    ['background', 'string', '—', 'Section background colour'],
    ['backgroundImage', 'string', '—', 'Section background image'],
    ['backgroundSize', 'string', "'cover'", 'background-size'],
    ['backgroundPosition', 'string', "'center'", 'background-position'],
    ['children', 'node', '—', 'Cards passed as children instead of items'],
    ['items', 'array', '—', 'Card prop objects, rendered as ProductCards'],
    ['rowGap', 'string | object', "{base:'medium-l', sm:'medium-l', md:'large-s'}", 'Token name, or per-breakpoint {base, sm, md}'],
    ['columnGap', 'string | object', "{base:'medium-s', sm:'medium-s', md:'medium-l'}", 'Token name, or per-breakpoint {base, sm, md}'],
    ['columns', 'number | object', '{base:1, sm:2, md:4}', 'Column count per breakpoint; md overrides the automatic choice'],
    ['desktopColumns', "'auto' | number", "'auto'", 'Desktop (≥768px) 3-vs-4 setting: auto renders exactly 3 cards 3-up and every other count 4-up; a number pins it. Outranked by columns.md'],
    ['lastRow', "'start' | 'center'", "'start'", 'A short final desktop row: left-aligned, or centred at full-row card width (requires items)'],
    ['centerIncomplete', 'bool', 'false', 'Deprecated — the old spelling of lastRow="center"'],
    ['oddCard', "'last' | 'first'", "'last'", 'Phones (376–767px), odd count only: which card takes the full row — last (1 2 | 3 4 | 5) or first (1 | 2 3 | 4 5); with tiles / spans per odd run of cards between the full rows. Desktop untouched'],
    ['layout', "'grid' | 'playful'", "'grid'", 'Playful (Figma OVER-LL Editions): on desktop the cards drop by alternating offsets (medium-l / medium-m / small-l, custom properties --products-grid-playful-1..3); works 3-up and 4-up; flush on mobile'],
    ['stagger', 'bool', 'false', 'Deprecated — the old spelling of layout="playful"'],
    ['cardProps', 'object', '—', 'Props merged into every ProductCard (an item\'s own props win), e.g. { frameMark } for a brand glyph on the frame'],
    ['moreLabel', 'string', '—', 'Swatch overflow label, passed to ProductCard'],
    ['tiles', 'array', '—', 'Opt-in content tiles between the cards: { id, position, variant: \'text\' | \'image\', title, titleAs, body, image, imageAlt, linkLabel, href, onClick }. position = 1-based slot of the final grid; past the end → appended. Needs items. Turns playful / lastRow off'],
    ['spans', 'object', '—', 'Opt-in { [position]: 2 } — the entry on that slot takes 2 desktop columns (photo height = its neighbours\'); mobile: full row. Needs items'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['style', 'object', '{}', 'Inline styles merged with the grid custom properties'],
  ],
  propsNote:
    'The automatic column count is the right default. Reach for desktopColumns for the common 3-vs-4 choice, and for columns.md only where card widths matter — the Accessories mixed grid pins 4 because its bulk cards span two tracks.',
  usage: [
    {
      title: 'A landing section',
      code: `import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';

<ProductsGrid
  brand="CLS"
  title="Vores kollektion"
  subtitle="Alle plagg er testet, vask efter vask."
  ctaLabel="Se mere"
  ctaIcon={<ArrowRight size={20} />}
  ctaOnClick={() => onMenuItemClick('menu_1', { toSchoolShop: true })}
  items={cards}
/>`,
    },
    {
      title: 'Column control',
      code: `// Automatic: exactly 3 cards → 3-up, anything else → 4-up
<ProductsGrid items={cards} />

// Pin the desktop count
<ProductsGrid items={cards} columns={{ base: 1, sm: 2, md: 3 }} />

// Centre a short final row (needs items, not children)
<ProductsGrid items={twoCards} centerIncomplete />`,
    },
    {
      title: 'Content tiles and a wide card (opt-in)',
      code: `// Positions are slots of the final grid (products + tiles), 1-based
<ProductsGrid
  desktopColumns={3}
  items={products}                 // from the API, unchanged
  spans={{ 1: 2 }}                 // slot 1 takes two desktop columns
  tiles={[
    { id: 'care', position: 6, variant: 'text',
      title: 'Designad för att bära minnen', body: [p1, p2],
      linkLabel: 'Discover more', href: '/care' },
    { id: 'iron', position: 9, variant: 'image',
      title: 'Fäst ditt märke med ett strykjärn', image, imageAlt,
      linkLabel: 'Discover more', href: '/iron' },
  ]}
/>`,
    },
  ],
};

export default function ProductsGridPage({ brand = 'CLS' }) {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Default" hint="8 products, title, subtitle, CTA" flush>
        <ProductsGrid
          brand={brand}
          title="Our Collection"
          subtitle="Alla plagg i vår kollektion är noggrant testade för att uppfylla våra höga kvalitetskrav, tvätt efter tvätt."
          ctaLabel="Discover More"
          ctaIcon={<ArrowRight size={20} />}
          items={DEMO_PRODUCTS}
        />
      </DemoCard>

      <DemoCard title="Custom gaps" hint="rowGap and columnGap set to medium-s" flush>
        <ProductsGrid
          brand={brand}
          title="Our Packages"
          subtitle="Spar op til 20% på pakkeløsninger"
          ctaLabel="Se mere"
          rowGap="medium-s"
          columnGap="medium-s"
          items={DEMO_PACKAGES}
        />
      </DemoCard>

      <DemoCard title="Dark theme with a background image" flush>
        <ProductsGrid
          brand={brand}
          theme="dark"
          backgroundImage={HERO_IMAGE}
          title="Our Packages"
          subtitle="Spar op til 20% på pakkeløsninger"
          ctaLabel="Discover More"
          items={DEMO_PACKAGES}
        />
      </DemoCard>

      {['left', 'center', 'right'].map((a) => (
        <DemoCard key={a} title={`Caption + align="${a}"`} hint="Figma 8746:73349" flush>
          <ProductsGrid brand={brand} caption="Collection" title="Standard" align={a} items={DEMO_PACKAGES} />
        </DemoCard>
      ))}

      {[['error', 'Trending', 'Oversize'], ['success', 'Spar op til 20%', 'Pakker']].map(([v, c, t]) => (
        <DemoCard key={v} title={`captionVariant="${v}"`} flush>
          <ProductsGrid brand={brand} caption={c} captionVariant={v} title={t} align="left" items={DEMO_PACKAGES} />
        </DemoCard>
      ))}

      <DemoCard title="Three cards" hint="automatic 3-up — a full row, no gap" flush>
        <ProductsGrid brand={brand} title="Three Up" items={DEMO_PACKAGES.slice(0, 3)} />
      </DemoCard>

      <DemoCard title="Minimal" hint="no title, no CTA" flush>
        <ProductsGrid brand={brand} items={DEMO_PACKAGES} paddingTop="large-s" paddingBottom="large-s" />
      </DemoCard>

      <DemoCard title="Two columns on desktop" flush>
        <ProductsGrid brand={brand} title="Two-Column Layout" columns={{ base: 1, sm: 2, md: 2 }} items={DEMO_PACKAGES} />
      </DemoCard>

      <DemoCard title="Playful layout" hint='layout="playful" — 3-up and 4-up; the offsets cycle per row' flush>
        <ProductsGrid brand={brand} caption="Trending" captionStyle="body" title="Editions" layout="playful" desktopColumns={3} items={DEMO_PRODUCTS.slice(0, 6)} />
        <ProductsGrid brand={brand} title="Four up" layout="playful" items={DEMO_PRODUCTS} paddingTop="large-s" />
      </DemoCard>

      <DemoCard title="Custom background colour" flush>
        <ProductsGrid
          brand={brand}
          background="var(--color-gray-025)"
          title="Featured Products"
          ctaLabel="View All"
          ctaVariant="outline"
          ctaIcon={<ArrowRight size={20} />}
          items={DEMO_PRODUCTS.slice(0, 4)}
        />
      </DemoCard>

      <DemoCard title="SEO text tile" hint="tiles — variant text on slot 6 (Figma OVER-LL 2699:18385)" flush>
        <ProductsGrid {...listingProps(brand)} items={LISTING.items.slice(0, 5)} tiles={[{ ...LISTING.textTile, position: 6 }]} />
      </DemoCard>

      <DemoCard title="SEO image tile" hint="tiles — variant image on slot 6 (Figma OVER-LL 2707:18924)" flush>
        <ProductsGrid {...listingProps(brand)} items={LISTING.items.slice(0, 5)} tiles={[{ ...LISTING.imageTile, position: 6 }]} />
      </DemoCard>

      <DemoCard title="Wide card" hint="spans={{ 1: 2 }} — two desktop columns, the photo as tall as its neighbours' (Figma OVER-LL 2699:16566)" flush>
        <ProductsGrid {...listingProps(brand)} items={[LISTING.wideItem, ...LISTING.items.slice(0, 4)]} spans={{ 1: 2 }} />
      </DemoCard>

      <DemoCard title='Odd count on phones: oddCard="first"' hint="3 cards, 376–767px: the FIRST card takes the full row instead of the last — the lead product on top, the pair under it (CLS-DK open shop listing setting). Desktop unchanged; narrow the window to see it" flush>
        <ProductsGrid brand={brand} title="Vogntrøjer" align="left" items={DEMO_PRODUCTS.slice(0, 3)} oddCard="first" />
      </DemoCard>

      <DemoCard title="Listing with all three" hint="wide card on slot 1, text tile on 6, image tile on 9 — resize to see the mobile rows" flush>
        <ProductsGrid
          {...listingProps(brand)}
          items={[LISTING.wideItem, ...LISTING.items]}
          spans={{ 1: 2 }}
          tiles={[{ ...LISTING.textTile, position: 6 }, { ...LISTING.imageTile, position: 9 }]}
        />
      </DemoCard>
    </ComponentPage>
  );
}

ProductsGridPage.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall']),
};
